import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import logoImage from '../../assets/images/logos/transserrano_viagens_pequeno.png';
import MobileDrawerFooter from './MobileDrawerFooter';
import { categoriasApi, tripApi, type CategoriaPublic, type Trip } from '../../services/api';

const MOBILE_NAV_DRAWER_ID = 'primary-navigation-drawer';

const mobileNavItemReveal = keyframes`
  from {
    opacity: 0;
    transform: translateX(12px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const MEGA_SLUG_ORDER = ['portugal', 'espanha', 'europa', 'asia', 'africa', 'america'] as const;

const HeaderContainer = styled.header`
  background: ${({ theme }) => theme.semantic.color.surface.raised};
  box-shadow: ${({ theme }) => theme.primitives.shadow.sm};
  position: fixed;
  top: var(--announcement-height);
  left: 0;
  right: 0;
  z-index: 1000;
  height: var(--header-height);
  display: flex;
  align-items: center;
`;

const HeaderContent = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.semantic.space.containerMax};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.semantic.space.md};
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  height: 100%;

  @media (min-width: ${({ theme }) => theme.primitives.breakpoint.sm}) {
    padding: 0 ${({ theme }) => theme.semantic.space.lg};
  }

  @media (min-width: ${({ theme }) => theme.primitives.breakpoint.lg}) {
    padding: 0 ${({ theme }) => theme.semantic.space.xl};
  }
`;

const Logo = styled.div`
  grid-column: 1;
  justify-self: start;
  flex-shrink: 0;

  a {
    display: flex;
    align-items: center;
  }

  img {
    height: ${({ theme }) => theme.primitives.space[10]};
    width: auto;

    @media (min-width: ${({ theme }) => theme.primitives.breakpoint.sm}) {
      height: ${({ theme }) => theme.primitives.space[12]};
    }
  }
`;

const NavMenu = styled.nav`
  grid-column: 2;
  justify-self: end;
  display: flex;
  align-items: center;
`;

const NavList = styled.ul`
  display: flex;
  gap: ${({ theme }) => theme.semantic.space.md};
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (min-width: ${({ theme }) => theme.primitives.breakpoint.lg}) {
    gap: ${({ theme }) => theme.semantic.space.xl};
  }

  @media (max-width: 1023px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const MobileDrawerStack = styled.div`
  @media (min-width: 1024px) {
    display: contents;
  }

  @media (max-width: 1023px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: calc(var(--header-height) + var(--announcement-height));
    right: 0;
    left: auto;
    bottom: 0;
    width: min(20rem, 82vw);
    max-width: 100%;
    background: ${({ theme }) => theme.semantic.color.surface.raised};
    z-index: 1000;
    box-shadow: ${({ theme }) => theme.primitives.shadow.drawer};
    transform: translateX(100%);
    visibility: hidden;
    overflow: hidden;
    transition:
      transform ${({ theme }) => theme.primitives.duration.slow}
        ${({ theme }) => theme.primitives.easing.ease},
      visibility 0ms ${({ theme }) => theme.primitives.duration.slow},
      box-shadow ${({ theme }) => theme.primitives.duration.slow}
        ${({ theme }) => theme.primitives.easing.ease};

    &.active {
      transform: translateX(0);
      visibility: visible;
      transition:
        transform ${({ theme }) => theme.primitives.duration.slow}
          ${({ theme }) => theme.primitives.easing.ease},
        visibility 0ms 0ms,
        box-shadow ${({ theme }) => theme.primitives.duration.slow}
          ${({ theme }) => theme.primitives.easing.ease};
    }
  }
`;

const MobileDrawerScroll = styled.div<{ $active: boolean }>`
  @media (min-width: 1024px) {
    display: contents;
  }

  @media (max-width: 1023px) {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    padding: ${({ theme }) => theme.semantic.space.lg};

    ${({ $active, theme }) =>
      $active
        ? css`
            @media (prefers-reduced-motion: no-preference) {
              & > ul > li {
                animation: ${mobileNavItemReveal} ${theme.primitives.duration.normal}
                  ${theme.primitives.easing.easeOut} forwards;
                opacity: 0;
              }
              & > ul > li:nth-child(1) {
                animation-delay: 0ms;
              }
              & > ul > li:nth-child(2) {
                animation-delay: 35ms;
              }
              & > ul > li:nth-child(3) {
                animation-delay: 70ms;
              }
              & > ul > li:nth-child(4) {
                animation-delay: 105ms;
              }
              & > ul > li:nth-child(5) {
                animation-delay: 140ms;
              }
              & > ul > li:nth-child(6) {
                animation-delay: 175ms;
              }
              & > ul > li:nth-child(7) {
                animation-delay: 210ms;
              }
              & > ul > li:nth-child(8) {
                animation-delay: 245ms;
              }
              & > ul > li:nth-child(9) {
                animation-delay: 280ms;
              }
              & > ul > li:nth-child(10) {
                animation-delay: 315ms;
              }
            }
          `
        : ''}
  }
`;

const MobileUpcomingLi = styled.li`
  @media (min-width: 1024px) {
    display: none;
  }

  list-style: none;
  margin: 0;
  padding: ${({ theme }) => theme.semantic.space.sm} 0 0;
  border-bottom: none;
`;

const MobileUpcomingLink = styled(Link)`
  display: block;
  min-width: 0;
  padding: ${({ theme }) => theme.semantic.space.sm} 0;
  text-decoration: none;
  color: ${({ theme }) => theme.semantic.color.text.primary};
  font-size: ${({ theme }) => theme.primitives.fontSize.sm};
  line-height: ${({ theme }) => theme.primitives.lineHeight.normal};
  border-top: 1px solid ${({ theme }) => theme.semantic.color.border.subtle};
  transition: color ${({ theme }) => theme.primitives.duration.fast}
    ${({ theme }) => theme.primitives.easing.ease};

  &:hover {
    color: ${({ theme }) => theme.semantic.color.brand.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.semantic.focusRing};
    border-radius: ${({ theme }) => theme.primitives.radius.xs};
  }
`;

const MobileUpcomingLabel = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.primitives.fontSize.xs};
  color: ${({ theme }) => theme.semantic.color.text.secondary};
  margin-bottom: ${({ theme }) => theme.primitives.space[1]};
`;

const MobileUpcomingTitle = styled.span`
  display: block;
  font-weight: ${({ theme }) => theme.primitives.fontWeight.semibold};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* Backdrop overlay shown behind the slide-in mobile menu */
const MenuOverlay = styled.div<{ $visible: boolean }>`
  display: none;

  @media (max-width: 1023px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 999;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
    transition: opacity ${({ theme }) => theme.primitives.duration.slow}
      ${({ theme }) => theme.primitives.easing.ease};
  }
`;

const MenuItem = styled.li`
  position: relative;
  white-space: nowrap;

  /* Desktop: reveal submenu on hover using opacity+visibility */
  @media (min-width: 1024px) {
    &:hover > ul,
    &:hover .submenu {
      opacity: 1;
      pointer-events: auto;
      visibility: visible;
      transition:
        opacity 160ms ease,
        transform 160ms ease,
        visibility 0ms 0ms;
    }

    &:hover > ul:not(.viagens-mega-submenu),
    &:hover .submenu:not(.viagens-mega-submenu) {
      transform: translateY(0);
    }

    &:hover > ul.viagens-mega-submenu,
    &:hover .submenu.viagens-mega-submenu {
      transform: translate(var(--mega-shift-x, 0px), 0);
    }
  }

  &.has-submenu {
    > a::after {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      border-right: 2px solid currentColor;
      border-bottom: 2px solid currentColor;
      transform: rotate(45deg);
      margin-left: ${({ theme }) => theme.semantic.space.inlineGap};
      margin-bottom: 3px;
      vertical-align: middle;
      transition: transform ${({ theme }) => theme.primitives.duration.normal}
        ${({ theme }) => theme.primitives.easing.ease};
      opacity: 0.7;
    }
    > a:hover::after {
      opacity: 1;
    }

    @media (min-width: ${({ theme }) => theme.primitives.breakpoint.lg}) {
      &:hover > a::after {
        transform: rotate(-135deg);
        margin-bottom: -1px;
      }
    }
    @media (max-width: 1023px) {
      > a {
        color: ${({ theme }) => theme.semantic.color.text.primary};
        font-weight: ${({ theme }) => theme.primitives.fontWeight.regular};
        &.active {
          color: ${({ theme }) => theme.semantic.color.brand.primary};
          font-weight: ${({ theme }) => theme.primitives.fontWeight.semibold};
        }
        &.open {
          color: ${({ theme }) => theme.semantic.color.brand.primary};
        }
        &.open::after {
          transform: rotate(-135deg);
          margin-bottom: -1px;
        }
      }
    }
  }

  a {
    color: ${({ theme }) => theme.semantic.color.text.primary};
    text-decoration: none;
    transition: color ${({ theme }) => theme.primitives.duration.normal}
      ${({ theme }) => theme.primitives.easing.ease};
    font-size: ${({ theme }) => theme.primitives.fontSize.base};
    padding: ${({ theme }) => theme.semantic.space.sm} 0;
    display: block;
    font-weight: ${({ theme }) => theme.primitives.fontWeight.regular};
    &:hover {
      color: ${({ theme }) => theme.semantic.color.brand.primary};
    }
    &.active {
      color: ${({ theme }) => theme.semantic.color.brand.primary};
      font-weight: ${({ theme }) => theme.primitives.fontWeight.semibold};
    }
  }

  /* Mobile: uniform tap targets, consistent separators */
  @media (max-width: 1023px) {
    width: 100%;
    padding: 0;
    border-bottom: 1px solid ${({ theme }) => theme.semantic.color.border.subtle};

    &:last-child {
      border-bottom: none;
    }

    a {
      min-height: 48px;
      display: flex;
      align-items: center;
      padding: 0;
      color: ${({ theme }) => theme.semantic.color.text.primary};
      font-weight: ${({ theme }) => theme.primitives.fontWeight.regular};
    }
    a.active {
      color: ${({ theme }) => theme.semantic.color.brand.primary};
      font-weight: ${({ theme }) => theme.primitives.fontWeight.semibold};
    }
  }
`;

const MegaSectionTitle = styled.p`
  font-size: ${({ theme }) => theme.semantic.text.label.size};
  font-weight: ${({ theme }) => theme.semantic.text.label.weight};
  line-height: ${({ theme }) => theme.semantic.text.label.lineHeight};
  color: ${({ theme }) => theme.semantic.color.text.secondary};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.primitives.letterSpacing.wide};
  margin-bottom: ${({ theme }) => theme.semantic.space.sm};
`;

const MenuToggle = styled.button<{ $isOpen: boolean }>`
  display: none;
  background: none;
  border: 1.5px solid ${({ theme }) => theme.semantic.color.border.subtle};
  border-radius: ${({ theme }) => theme.primitives.radius.sm};
  cursor: pointer;
  width: 40px;
  height: 40px;
  padding: 0;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.semantic.color.text.primary};
  transition:
    background-color ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.ease},
    border-color ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.ease};
  z-index: 1001;
  flex-shrink: 0;

  svg {
    display: block;
    transition: opacity ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.ease};
  }

  &:hover {
    background: ${({ theme }) => theme.semantic.color.surface.page};
    border-color: ${({ theme }) =>
      theme.semantic.color.border.default ?? theme.semantic.color.border.subtle};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.semantic.focusRing};
  }

  @media (max-width: 1023px) {
    display: flex;
    border-radius: ${({ theme }) => theme.primitives.radius.none};
  }
`;

const SubMenu = styled.ul`
  list-style: none;
  position: absolute;
  top: 100%;
  left: 0;
  background: ${({ theme }) => theme.semantic.color.surface.raised};
  min-width: 200px;
  box-shadow: ${({ theme }) => theme.primitives.shadow.md};
  border-radius: ${({ theme }) => theme.primitives.radius.xs};
  padding: ${({ theme }) => theme.semantic.space.sm} 0;
  z-index: 100;
  /* Desktop animation default: hidden */
  opacity: 0;
  transform: translateY(-6px);
  pointer-events: none;
  visibility: hidden;
  transition:
    opacity 160ms ease,
    transform 160ms ease,
    visibility 0ms 160ms;

  @media (max-width: 1023px) {
    /* Mobile: max-height expand/collapse, always visible in DOM */
    position: static;
    display: block;
    max-height: 0;
    overflow: hidden;
    opacity: 1;
    transform: none;
    pointer-events: auto;
    visibility: visible;
    transition:
      max-height 280ms cubic-bezier(0.4, 0, 0.2, 1),
      padding 200ms ease,
      margin 200ms ease;
    box-shadow: none;
    padding: 0 0 0 ${({ theme }) => theme.semantic.space.md};
    border-left: 2px solid ${({ theme }) => theme.semantic.color.brand.primary};
    margin-top: 0;
    border-radius: 0;

    &.open {
      max-height: 600px;
      padding-top: ${({ theme }) => theme.semantic.space.sm};
      padding-bottom: ${({ theme }) => theme.semantic.space.sm};
      margin-top: ${({ theme }) => theme.semantic.space.sm};
    }

    a {
      font-size: ${({ theme }) => theme.primitives.fontSize.sm};
      min-height: 40px;
      padding: 0;
      display: flex;
      align-items: center;
      color: ${({ theme }) => theme.semantic.color.text.secondary};

      &:hover {
        color: ${({ theme }) => theme.semantic.color.brand.primary};
        background: transparent;
      }
    }
  }

  a {
    padding: ${({ theme }) => `${theme.primitives.space[3]} ${theme.primitives.space[6]}`};
    display: block;
    color: ${({ theme }) => theme.semantic.color.text.primary};
    transition:
      color ${({ theme }) => theme.primitives.duration.normal}
        ${({ theme }) => theme.primitives.easing.ease},
      background-color ${({ theme }) => theme.primitives.duration.normal}
        ${({ theme }) => theme.primitives.easing.ease};
    white-space: nowrap;
    font-size: ${({ theme }) => theme.primitives.fontSize.sm};

    &:hover {
      background: ${({ theme }) => theme.semantic.color.surface.page};
      color: ${({ theme }) => theme.semantic.color.brand.primary};
    }
  }
`;

const ViagensMegaSubMenu = styled(SubMenu)`
  & > li.viagens-mega-wrap {
    list-style: none;
    margin: 0;
    padding: 0;
    width: 100%;

    @media (max-width: 1023px) {
      display: none;
    }
  }

  & > li.viagens-mobile-only {
    @media (min-width: 1024px) {
      display: none;
    }
  }

  @media (min-width: 1024px) {
    /* Anchor to trigger right edge so the panel grows leftward (avoids clipping off the viewport right) */
    left: auto;
    right: 0;
    transform: translate(var(--mega-shift-x, 0px), -6px);
    min-width: min(760px, calc(100vw - ${({ theme }) => theme.primitives.space[6]} * 2));
    max-width: min(1000px, calc(100vw - ${({ theme }) => theme.primitives.space[6]} * 2));
    padding: ${({ theme }) => theme.semantic.space.md};
    border-radius: ${({ theme }) => theme.primitives.radius.md};
  }
`;

const MegaDesktop = styled.div`
  display: none;

  @media (min-width: 1024px) {
    display: block;
  }
`;

const MegaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.primitives.space[2]};
  margin-bottom: ${({ theme }) => theme.semantic.space.sm};

  /* SubMenu applies padding to every <a> — removes white "frame" around photo cards */
  && a {
    padding: 0;
    white-space: normal;
    font-size: inherit;
  }

  && a:hover {
    background: transparent;
    color: #ffffff;
  }
`;

const MegaCardInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  overflow: hidden;
`;

const MegaCardBg = styled.div<{ $imageUrl?: string; $fallback: string }>`
  position: absolute;
  inset: 0;
  background: ${({ $imageUrl, $fallback }) =>
    $imageUrl ? `url('${$imageUrl}') center/cover no-repeat` : $fallback};
  transition: transform 420ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  will-change: transform;
`;

const MegaCardScrim = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.92) 0%,
    rgba(0, 0, 0, 0.7) 35%,
    rgba(0, 0, 0, 0.3) 60%,
    rgba(0, 0, 0, 0.05) 100%
  );
`;

const MegaCardContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;
  padding: ${({ theme }) => theme.semantic.space.md};
`;

const MegaCard = styled(Link)`
  position: relative;
  display: block;
  height: 10.25rem;
  border-radius: ${({ theme }) => theme.primitives.radius.md};
  text-decoration: none;
  overflow: hidden;
  box-shadow: none;
  transition:
    transform 240ms ${({ theme }) => theme.primitives.easing.easeOut},
    box-shadow 240ms ${({ theme }) => theme.primitives.easing.easeOut};

  /* Use && to beat SubMenu a:hover specificity */
  && {
    color: #ffffff;
    background: transparent;
  }

  &&:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
    color: #ffffff;
    background: transparent;

    ${MegaCardBg} {
      transform: scale(1.06);
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.semantic.focusRing};
  }
`;

const MegaCardTitle = styled.span`
  display: block;
  color: #ffffff;
  font-size: ${({ theme }) => theme.primitives.fontSize.lg};
  font-weight: ${({ theme }) => theme.primitives.fontWeight.bold};
  line-height: 1.2;
  text-shadow:
    0 1px 8px rgba(0, 0, 0, 0.95),
    0 2px 20px rgba(0, 0, 0, 0.7);
  letter-spacing: -0.01em;
`;

const MegaCardMeta = styled.span`
  display: block;
  color: rgba(255, 255, 255, 0.88);
  font-size: ${({ theme }) => theme.primitives.fontSize.xs};
  font-weight: ${({ theme }) => theme.primitives.fontWeight.medium};
  margin-top: 2px;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.9);
`;

const MegaPrivateBanner = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.semantic.space.lg};
  padding: ${({ theme }) => `${theme.semantic.space.lg} ${theme.semantic.space.xl}`};
  border-radius: ${({ theme }) => theme.primitives.radius.md};
  text-decoration: none;
  overflow: hidden;
  position: relative;
  transition:
    transform 200ms ${({ theme }) => theme.primitives.easing.easeOut},
    box-shadow 200ms ${({ theme }) => theme.primitives.easing.easeOut};

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -2%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.07);
    pointer-events: none;
  }

  /* && beats SubMenu a { background/color } specificity (class+element vs class+class) */
  && {
    background: linear-gradient(110deg, #0d1f45 0%, #1a3a8f 55%, #2451c4 100%);
    color: #ffffff;
  }

  &&:hover {
    background: linear-gradient(110deg, #0a1830 0%, #16307a 55%, #1e46b0 100%);
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.semantic.focusRing};
  }
`;

const MegaPrivateBannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.primitives.space[1]};
  min-width: 0;
  flex: 1;
`;

const MegaPrivateBannerTitle = styled.span`
  font-size: ${({ theme }) => theme.primitives.fontSize.md};
  font-weight: ${({ theme }) => theme.primitives.fontWeight.bold};
  color: #ffffff;
  line-height: 1.2;
`;

const MegaPrivateBannerSub = styled.span`
  font-size: ${({ theme }) => theme.primitives.fontSize.sm};
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.45;
  margin-top: ${({ theme }) => theme.primitives.space[1]};
`;

const MegaPrivateBannerCta = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.primitives.space[2]};
  margin-top: ${({ theme }) => theme.semantic.space.sm};
  font-size: ${({ theme }) => theme.primitives.fontSize.sm};
  font-weight: ${({ theme }) => theme.primitives.fontWeight.semibold};
  color: #ffffff;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.42);
  padding: ${({ theme }) => `${theme.primitives.space[3]} ${theme.primitives.space[5]}`};
  border-radius: ${({ theme }) => theme.primitives.radius.md};
  white-space: nowrap;
  flex-shrink: 0;
  align-self: center;
  transition:
    background 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease;

  ${MegaPrivateBanner}:hover & {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.55);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  }
`;

const MegaConditionsLink = styled(Link)`
  display: block;
  text-align: center;
  padding-top: ${({ theme }) => theme.semantic.space.sm};
  font-size: ${({ theme }) => theme.primitives.fontSize.xs};
  color: ${({ theme }) =>
    theme.semantic.color.text.tertiary ?? theme.semantic.color.text.secondary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.semantic.color.text.secondary};
  }
`;

const VIAGENS_MEGA_VIEWPORT_PAD_PX = 24;

function formatTripMonthYear(trip: Trip): string | null {
  const raw = trip.data_inicio ?? trip.data ?? trip.data_fim;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('pt-PT', { month: 'long', year: 'numeric' }).format(d);
}

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [viagensCategories, setViagensCategories] = useState<CategoriaPublic[]>([]);
  const [viagensMegaHover, setViagensMegaHover] = useState(false);
  const [upcomingTeaser, setUpcomingTeaser] = useState<Trip | null>(null);
  const viagensMegaRef = useRef<HTMLUListElement>(null);
  const upcomingFetchStarted = useRef(false);
  const location = useLocation();

  useEffect(() => {
    categoriasApi
      .getAll()
      .then(setViagensCategories)
      .catch(() => setViagensCategories([]));
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenSubmenu(null);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isMenuOpen || upcomingFetchStarted.current) return;
    upcomingFetchStarted.current = true;
    tripApi.getUpcoming(1).then((trips) => {
      if (trips.length > 0) setUpcomingTeaser(trips[0]);
    });
  }, [isMenuOpen]);

  useLayoutEffect(() => {
    const el = viagensMegaRef.current;
    if (!el || !viagensMegaHover || window.innerWidth < 1024) {
      el?.style.removeProperty('--mega-shift-x');
      return;
    }

    const applyShift = () => {
      const menu = viagensMegaRef.current;
      if (!menu) return;
      if (window.innerWidth < 1024) {
        menu.style.removeProperty('--mega-shift-x');
        return;
      }
      const rect = menu.getBoundingClientRect();
      let shift = 0;
      const vw = window.innerWidth;
      const pad = VIAGENS_MEGA_VIEWPORT_PAD_PX;
      if (rect.right > vw - pad) {
        shift += vw - pad - rect.right;
      }
      if (rect.left + shift < pad) {
        shift += pad - (rect.left + shift);
      }
      if (shift !== 0) {
        menu.style.setProperty('--mega-shift-x', `${shift}px`);
      } else {
        menu.style.removeProperty('--mega-shift-x');
      }
    };

    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(applyShift);
    });
    window.addEventListener('resize', applyShift);
    return () => {
      cancelAnimationFrame(outerRaf);
      cancelAnimationFrame(innerRaf);
      window.removeEventListener('resize', applyShift);
      el.style.removeProperty('--mega-shift-x');
    };
  }, [viagensMegaHover, viagensCategories]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
        setOpenSubmenu(null);
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const toggleSubmenu = (menuId: string) => {
    setOpenSubmenu(openSubmenu === menuId ? null : menuId);
  };

  const REGION_IMAGES: Record<string, string> = {
    portugal:
      'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?auto=format&fit=crop&w=400&q=80',
    espanha:
      'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?auto=format&fit=crop&w=400&q=80',
    europa:
      'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?auto=format&fit=crop&w=400&q=80',
    asia: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=400&q=80',
    africa:
      'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=400&q=80',
    america:
      'https://images.unsplash.com/photo-1483729558449-99ef09a8c325?auto=format&fit=crop&w=400&q=80',
  };

  const megaCategories = MEGA_SLUG_ORDER.map((slug) => {
    const c = viagensCategories.find((x) => x.slug === slug);
    return {
      slug,
      nome: c?.nome ?? slug.charAt(0).toUpperCase() + slug.slice(1),
      count: Number(c?.total_viagens ?? 0),
      imageUrl: REGION_IMAGES[slug],
    };
  });

  const upcomingMonthYear = upcomingTeaser ? formatTripMonthYear(upcomingTeaser) : null;

  return (
    <>
      <HeaderContainer>
        <HeaderContent>
          <Logo>
            <Link to="/">
              <img src={logoImage} alt="Transserrano Viagens Logo" />
            </Link>
          </Logo>
          <NavMenu>
            <MenuToggle
              type="button"
              $isOpen={isMenuOpen}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={isMenuOpen}
              aria-controls={MOBILE_NAV_DRAWER_ID}
            >
              {isMenuOpen ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <line
                    x1="4"
                    y1="4"
                    x2="16"
                    y2="16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="16"
                    y1="4"
                    x2="4"
                    y2="16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <line
                    x1="3"
                    y1="5"
                    x2="17"
                    y2="5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="3"
                    y1="10"
                    x2="17"
                    y2="10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="3"
                    y1="15"
                    x2="17"
                    y2="15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </MenuToggle>
            <MobileDrawerStack
              id={MOBILE_NAV_DRAWER_ID}
              role={isMenuOpen ? 'dialog' : undefined}
              aria-modal={isMenuOpen || undefined}
              aria-label="Menu principal"
              className={isMenuOpen ? 'active' : ''}
            >
              <MobileDrawerScroll $active={isMenuOpen}>
                <NavList>
                  <MenuItem>
                    <Link to="/calendario" className={isActive('/calendario') ? 'active' : ''}>
                      Calendário
                    </Link>
                  </MenuItem>
                  <MenuItem
                    className="has-submenu"
                    onMouseEnter={() => {
                      if (window.innerWidth >= 1024) setViagensMegaHover(true);
                    }}
                    onMouseLeave={() => setViagensMegaHover(false)}
                  >
                    <Link
                      to="/viagens"
                      className={`${isActive('/viagens') ? 'active' : ''} ${openSubmenu === 'viagens' ? 'open' : ''}`}
                      onClick={(e) => {
                        if (window.innerWidth < 1024) {
                          e.preventDefault();
                          toggleSubmenu('viagens');
                        }
                      }}
                    >
                      Viagens
                    </Link>
                    <ViagensMegaSubMenu
                      ref={viagensMegaRef}
                      className={`submenu viagens-mega-submenu ${openSubmenu === 'viagens' ? 'open' : ''}`}
                    >
                      <li className="viagens-mega-wrap">
                        <MegaDesktop>
                          <MegaSectionTitle>Explorar por região</MegaSectionTitle>
                          <MegaGrid>
                            {megaCategories.map(({ slug, nome, count, imageUrl }) => (
                              <MegaCard key={slug} to={`/viagens?categoria=${slug}`}>
                                <MegaCardInner>
                                  <MegaCardBg
                                    $imageUrl={imageUrl}
                                    $fallback={`linear-gradient(145deg, #1e3a8a 0%, #1e40af 100%)`}
                                  />
                                  <MegaCardScrim />
                                  <MegaCardContent>
                                    <MegaCardTitle>{nome}</MegaCardTitle>
                                    <MegaCardMeta>
                                      {count === 1 ? '1 viagem' : `${count} viagens`}
                                    </MegaCardMeta>
                                  </MegaCardContent>
                                </MegaCardInner>
                              </MegaCard>
                            ))}
                          </MegaGrid>
                          <MegaPrivateBanner to="/viagens/viagens-privadas">
                            <MegaPrivateBannerText>
                              <MegaPrivateBannerTitle>Viagens Privadas</MegaPrivateBannerTitle>
                              <MegaPrivateBannerSub>
                                Percursos únicos, criados à medida do seu grupo
                              </MegaPrivateBannerSub>
                            </MegaPrivateBannerText>
                            <MegaPrivateBannerCta>Organizar a minha viagem →</MegaPrivateBannerCta>
                          </MegaPrivateBanner>
                          <MegaConditionsLink to="/viagens/condicoes-gerais">
                            Condições Gerais
                          </MegaConditionsLink>
                        </MegaDesktop>
                      </li>
                      <li className="viagens-mobile-only">
                        <Link to="/viagens?categoria=portugal">Portugal</Link>
                      </li>
                      <li className="viagens-mobile-only">
                        <Link to="/viagens?categoria=espanha">Espanha</Link>
                      </li>
                      <li className="viagens-mobile-only">
                        <Link to="/viagens?categoria=europa">Europa</Link>
                      </li>
                      <li className="viagens-mobile-only">
                        <Link to="/viagens?categoria=asia">Ásia</Link>
                      </li>
                      <li className="viagens-mobile-only">
                        <Link to="/viagens?categoria=africa">África</Link>
                      </li>
                      <li className="viagens-mobile-only">
                        <Link to="/viagens?categoria=america">América</Link>
                      </li>
                      <li className="viagens-mobile-only">
                        <Link to="/viagens/condicoes-gerais">Condições Gerais</Link>
                      </li>
                      <li className="viagens-mobile-only">
                        <Link to="/viagens/viagens-privadas">Viagens Privadas</Link>
                      </li>
                    </ViagensMegaSubMenu>
                  </MenuItem>
                  <MenuItem className="has-submenu">
                    <Link
                      to="/sobre-nos"
                      className={`${isActive('/sobre-nos') ? 'active' : ''} ${openSubmenu === 'sobre' ? 'open' : ''}`}
                      onClick={(e) => {
                        if (window.innerWidth < 1024) {
                          e.preventDefault();
                          toggleSubmenu('sobre');
                        }
                      }}
                    >
                      A Trans Serrano
                    </Link>
                    <SubMenu className={openSubmenu === 'sobre' ? 'open' : ''}>
                      <li>
                        <Link to="/sobre-nos">Sobre nós</Link>
                      </li>
                      <li>
                        <a
                          href="https://transserrano.com"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Trans Serrano Aventura
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://www.transserrano.com/hotel-gois/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Casa de Campo Vale do Ceira
                        </a>
                      </li>
                      <li>
                        <a href="https://goiscamping.com" target="_blank" rel="noopener noreferrer">
                          Góis Camping
                        </a>
                      </li>
                      <li>
                        <Link to="/viagens/condicoes-gerais">Condições Gerais</Link>
                      </li>
                      <li>
                        <Link to="/sobre-nos#faq">FAQ</Link>
                      </li>
                    </SubMenu>
                  </MenuItem>
                  <MenuItem>
                    <Link to="/contacto" className={isActive('/contacto') ? 'active' : ''}>
                      Contacto
                    </Link>
                  </MenuItem>
                  {upcomingTeaser?.slug ? (
                    <MobileUpcomingLi>
                      <MobileUpcomingLink to={`/viagens/${upcomingTeaser.slug}`}>
                        <MobileUpcomingLabel>Próxima saída</MobileUpcomingLabel>
                        <MobileUpcomingTitle>
                          {upcomingTeaser.titulo}
                          {upcomingMonthYear ? ` · ${upcomingMonthYear}` : ''}
                        </MobileUpcomingTitle>
                      </MobileUpcomingLink>
                    </MobileUpcomingLi>
                  ) : null}
                </NavList>
              </MobileDrawerScroll>
              <MobileDrawerFooter />
            </MobileDrawerStack>
          </NavMenu>
        </HeaderContent>
      </HeaderContainer>
      <MenuOverlay $visible={isMenuOpen} onClick={() => setIsMenuOpen(false)} />
    </>
  );
};

export default Header;
